// src/componentes/Escala11/Competicao/CompetitionDetailsModal/CompetitionPositionManager.tsx

import React, { useState } from "react";
import {
  Button,
  TextField,
  Box,
  Typography,
  //  Grid, // REMOVIDO: Eliminando o componente Grid
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SaveIcon from "@mui/icons-material/Save";

// --- INTERFACES LOCAIS (inalteradas) ---
interface Player {
  id: string;
  name: string;
  description: string;
  positionId: string;
}
interface Position {
  id: string;
  name: string;
  maxSelections: number;
  pointWeight: number;
  displayOrder: number;
  players: Player[];
}

interface CompetitionPositionManagerProps {
  token: string;
  competitionId: string;
  competitionTitle: string;
  positions: Position[];
  onPositionsChanged: () => void;
}

interface NewPlayerInBatch {
  positionId: string;
  name: string;
  description: string;
}
// --- FIM DAS INTERFACES ---

const API_BASE_URL = "https://api.sauhde.com.br/admin/scale-competitions";
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const CompetitionPositionManager: React.FC<CompetitionPositionManagerProps> = ({
  token,
  competitionId,
  positions,
  onPositionsChanged,
  competitionTitle,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isAdding, setIsAdding] = useState(false);
  const [editingPosition, setEditingPosition] = useState<Position | null>(null);

  // Estados para Adicionar Posição (POST)
  const [newName, setNewName] = useState("");
  const [newMaxSelections, setNewMaxSelections] = useState<number | string>(1);
  const [newPointWeight, setNewPointWeight] = useState<number | string>(1.0);
  const [newDisplayOrder, setNewDisplayOrder] = useState<number | string>(1);

  // Estados para Edição de Posição (PUT)
  const [editName, setEditName] = useState("");
  const [editPointWeight, setEditPointWeight] = useState<number | string>(1.0);

  // --- ESTADOS PARA JOGADOR (Batch) ---
  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [newPlayerName, setNewPlayerName] = useState("");
  const [newPlayerDescription, setNewPlayerDescription] = useState("");
  const initialPositionId = positions.length > 0 ? positions[0].id : "";
  const [selectedPositionId, setSelectedPositionId] =
    useState(initialPositionId);

  const [newPlayersBatch, setNewPlayersBatch] = useState<NewPlayerInBatch[]>(
    []
  );
  const [isPostingBatch, setIsPostingBatch] = useState(false);
  const [postingProgress, setPostingProgress] = useState(0);

  const [expandedPositionId, setExpandedPositionId] = useState<string | null>(
    null
  );

  const [deletingPlayer, setDeletingPlayer] = useState<Player | null>(null);

  const [editingPlayer, setEditingPlayer] = useState<Player | null>(null);
  const [editPlayerName, setEditPlayerName] = useState("");
  const [editPlayerDescription, setEditPlayerDescription] = useState("");
  // ------------------------------------

  const [feedback, setFeedback] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({ open: false, message: "", severity: "success" });

  const handleCloseFeedback = () => {
    setFeedback({ ...feedback, open: false });
  };

  // --- Lógicas de CRUD (Posição) (inalteradas) ---

  const handleAddPosition = async () => {
    if (!newName || Number(newMaxSelections) < 1) {
      setFeedback({
        open: true,
        message:
          "Preencha todos os campos obrigatórios e 'Número de itens' deve ser no mínimo 1.",
        severity: "error",
      });
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/${competitionId}/positions`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: newName,
            maxSelections: Number(newMaxSelections),
            pointWeight: Number(newPointWeight),
            displayOrder: Number(newDisplayOrder),
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Erro ao adicionar posição.");
      }

      setFeedback({
        open: true,
        message: `Posição '${newName}' adicionada com sucesso!`,
        severity: "success",
      });
      setIsAdding(false);
      setNewName("");
      setNewMaxSelections(1);
      setNewPointWeight(1.0);
      setNewDisplayOrder(1);
      onPositionsChanged();
    } catch (error) {
      setFeedback({
        open: true,
        message: `Falha ao adicionar: ${
          error instanceof Error ? error.message : "Erro desconhecido"
        }`,
        severity: "error",
      });
    }
  };

  const handleEditPosition = async () => {
    const editingId = editingPosition?.id;
    if (!editingId || !editName || Number(editPointWeight) <= 0) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/${competitionId}/positions/${editingId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: editName,
            pointWeight: Number(editPointWeight),
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Erro ao editar posição.");
      }

      setFeedback({
        open: true,
        message: `Posição '${editName}' editada com sucesso!`,
        severity: "success",
      });
      setEditingPosition(null);
      onPositionsChanged();
    } catch (error) {
      setFeedback({
        open: true,
        message: `Falha ao editar: ${
          error instanceof Error ? error.message : "Erro desconhecido"
        }`,
        severity: "error",
      });
    }
  };

  const handleDeletePosition = async (
    positionId: string,
    positionName: string
  ) => {
    if (
      !window.confirm(
        `Tem certeza que deseja DELETAR a posição "${positionName}"? Esta ação é irreversível.`
      )
    ) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/${competitionId}/positions/${positionId}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.status !== 204 && !response.ok) {
        throw new Error("Erro ao deletar posição.");
      }

      setFeedback({
        open: true,
        message: `Posição "${positionName}" deletada com sucesso!`,
        severity: "success",
      });
      onPositionsChanged();
    } catch (error) {
      setFeedback({
        open: true,
        message: `Falha ao deletar: ${
          error instanceof Error ? error.message : "Erro desconhecido"
        }`,
        severity: "error",
      });
    }
  };

  // --- Lógicas de CRUD (Jogador) (inalteradas) ---

  const handleAddPlayerToBatch = () => {
    if (!newPlayerName || !selectedPositionId) {
      setFeedback({
        open: true,
        message: "O Nome do Item e a Posição são obrigatórios.",
        severity: "error",
      });
      return;
    }

    const newPlayer: NewPlayerInBatch = {
      positionId: selectedPositionId,
      name: newPlayerName,
      description: newPlayerDescription,
    };

    setNewPlayersBatch((prev) => [...prev, newPlayer]);

    setFeedback({
      open: true,
      message: `Item '${newPlayerName}' adicionado ao lote! (${
        newPlayersBatch.length + 1
      } no total)`,
      severity: "info",
    });

    setNewPlayerName("");
    setNewPlayerDescription("");
  };

  const handlePostPlayersBatch = async () => {
    if (newPlayersBatch.length === 0) {
      setIsPlayerModalOpen(false);
      return;
    }

    setIsPostingBatch(true);
    let successCount = 0;
    let errorCount = 0;
    const totalPlayers = newPlayersBatch.length;

    try {
      for (let i = 0; i < totalPlayers; i++) {
        const player = newPlayersBatch[i];
        const progress = Math.ceil(((i + 1) / totalPlayers) * 100);
        setPostingProgress(progress);

        try {
          const response = await fetch(
            `${API_BASE_URL}/${competitionId}/players`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(player),
            }
          );

          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
            console.error(
              `Falha ao enviar item '${player.name}':`,
              await response.text()
            );
          }
        } catch (error) {
          errorCount++;
          console.error(`Erro de rede ao enviar item '${player.name}':`, error);
        }

        if (i < totalPlayers - 1) {
          await delay(1000);
        }
      }

      setFeedback({
        open: true,
        message: `Lote finalizado! ${successCount} sucesso(s), ${errorCount} falha(s).`,
        severity: errorCount > 0 ? "warning" : "success",
      });

      setNewPlayersBatch([]);
      setIsPlayerModalOpen(false);
      onPositionsChanged();
    } catch (error) {
      setFeedback({
        open: true,
        message: `Falha grave no processamento do lote: ${
          error instanceof Error ? error.message : "Erro desconhecido"
        }`,
        severity: "error",
      });
    } finally {
      setIsPostingBatch(false);
      setPostingProgress(0);
    }
  };

  // Lógica para Editar Jogador (mantida)
  const handleEditPlayer = async () => {
    if (!editingPlayer || !editPlayerName) {
      setFeedback({
        open: true,
        message: "O Nome do Item é obrigatório.",
        severity: "error",
      });
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/${competitionId}/players/${editingPlayer.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: editPlayerName,
            description: editPlayerDescription,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Erro ao editar Item.");
      }

      setFeedback({
        open: true,
        message: `Item '${editPlayerName}' editado com sucesso!`,
        severity: "success",
      });
      setEditingPlayer(null);
      onPositionsChanged();
    } catch (error) {
      setFeedback({
        open: true,
        message: `Falha ao editar Item: ${
          error instanceof Error ? error.message : "Erro desconhecido"
        }`,
        severity: "error",
      });
    }
  };

  const handleDeletePlayer = async () => {
    if (!deletingPlayer) return;

    const { id: playerId, name: playerName } = deletingPlayer;

    try {
      const response = await fetch(
        `${API_BASE_URL}/${competitionId}/players/${playerId}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status !== 204 && !response.ok) {
        throw new Error("Erro ao deletar Item.");
      }

      setFeedback({
        open: true,
        message: `Item "${playerName}" deletado com sucesso!`,
        severity: "success",
      });
      setDeletingPlayer(null);
      onPositionsChanged();
    } catch (error) {
      setFeedback({
        open: true,
        message: `Falha ao deletar Item: ${
          error instanceof Error ? error.message : "Erro desconhecido"
        }`,
        severity: "error",
      });
      setDeletingPlayer(null);
    }
  };

  // --- Funções de Abertura de Modals e Expansão (mantidas) ---

  const handleOpenEditPosition = (position: Position) => {
    setEditingPosition(position);
    setEditName(position.name);
    setEditPointWeight(position.pointWeight);
  };

  const handleOpenEditPlayer = (player: Player) => {
    setEditingPlayer(player);
    setEditPlayerName(player.name);
    setEditPlayerDescription(player.description);
  };

  const handleTogglePlayerList = (positionId: string) => {
    setExpandedPositionId(
      positionId === expandedPositionId ? null : positionId
    );
  };

  const handleClosePlayerModal = () => {
    if (isPostingBatch) return;

    setIsPlayerModalOpen(false);
    setNewPlayerName("");
    setNewPlayerDescription("");
    setSelectedPositionId(positions.length > 0 ? positions[0].id : "");
    setNewPlayersBatch([]);
  };

  // --- Componente de Ações da Posição (reusável e responsivo) (inalterado) ---
  const PositionActions = (p: Position) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        mt: isMobile ? 1 : 0,
      }}
    >
      {/* Botão Editar Posição */}
      <IconButton size="small" onClick={() => handleOpenEditPosition(p)}>
        <EditIcon
          sx={{
            fontSize: 16,
            color: "#008870",
            "&:hover": {
              color: "#005848ff",
            },
          }}
        />
      </IconButton>
      {/* Botão Deletar Posição */}
      <IconButton
        size="small"
        onClick={() => handleDeletePosition(p.id, p.name)}
      >
        <DeleteIcon sx={{ fontSize: 16, color: "error.main" }} />
      </IconButton>
    </Box>
  );

  return (
    <Box>
      {/* Botões de Ação (Posição e Jogador) */}
      <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setIsAdding(true)}
          sx={{
            color: "#008870",
            borderColor: "#008870",
            "&:hover": {
              color: "#005848ff",
              borderColor: "#005848ff",
            },
          }}
        >
          Nova Posição
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<PersonAddIcon />}
          onClick={() => {
            if (positions.length === 0) {
              setFeedback({
                open: true,
                message: "Crie ao menos uma posição antes de adicionar itens.",
                severity: "warning",
              });
              return;
            }
            setIsPlayerModalOpen(true);
            if (positions.length > 0) {
              setSelectedPositionId(positions[0].id);
            }
          }}
        >
          Adicionar Item
        </Button>
      </Box>
      {/* Seção de Adição de Posição (POST) - SUBSTITUINDO GRID */}
      {isAdding && (
        <Box
          sx={{
            p: 2,
            mb: 2,
            border: "1px solid #ccc",
            borderRadius: 1,
            bgcolor: "action.hover",
          }}
        >
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ fontWeight: "bold" }}
          >
            Nova Posição na Competição "{competitionTitle}"
          </Typography>

          {/* Inputs em 4 colunas no desktop, 2 em tablet, 1 em mobile */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 2, // Simula spacing={2}
              mb: 2,
              // Estilo de item responsivo: xs=12, sm=6, md=3
              "& > div": {
                width: {
                  xs: "100%", // 1 coluna em xs
                  sm: "calc(50% - 8px)", // 2 colunas em sm (50% - metade do gap)
                  md: "calc(25% - 12px)", // 4 colunas em md (25% - 3/4 do gap)
                },
              },
            }}
          >
            {/* Campo 1: Nome da Posição */}
            <TextField
              label="Nome da Posição"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              // fullWidth é coberto pelo width responsivo acima
            />
            {/* Campo 2: Número de itens (Máx. Seleções) */}
            <TextField
              label="Número de itens"
              type="number"
              inputProps={{ min: 1 }}
              value={newMaxSelections}
              onChange={(e) => setNewMaxSelections(e.target.value)}
            />
            {/* Campo 3: Peso (Point Weight) */}
            <TextField
              label="Peso"
              type="number"
              inputProps={{ min: 0.1, step: 0.1 }}
              value={newPointWeight}
              onChange={(e) => setNewPointWeight(e.target.value)}
              helperText="Valor de pontuação."
            />
            {/* Campo 4: Ordem de Amostragem */}
            <TextField
              label="Ordem de Amostragem"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={newDisplayOrder}
              onChange={(e) => setNewDisplayOrder(e.target.value)}
            />
          </Box>

          {/* Botões de Ação da Posição (Salvar/Cancelar) */}
          <Box>
            <Button
              onClick={handleAddPosition}
              variant="contained"
              sx={{
                mr: 1,
                backgroundColor: "#008870",
                "&:hover": { backgroundColor: "#005848ff" },
              }}
            >
              Salvar Posição
            </Button>
            <Button
              onClick={() => setIsAdding(false)}
              variant="outlined"
              sx={{
                color: "#008870",
                borderColor: "#008870",
                "&:hover": { color: "#005848ff", borderColor: "#005848ff" },
              }}
            >
              Cancelar
            </Button>
          </Box>
        </Box>
      )}
      Posições Cadastradas
      {/* Lista de Posições e Itens (inalterada) */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {positions.length === 0 ? (
          <Alert severity="info" sx={{ width: "100%", mt: 1 }}>
            Nenhuma posição cadastrada nesta competição.
          </Alert>
        ) : (
          <List
            component={Box}
            sx={{
              width: "100%",
              border: "1px solid #eee",
              borderRadius: 1,
              bgcolor: "background.paper",
            }}
          >
            {positions.map((p) => {
              const playersInPosition = p.players || [];
              const isExpanded = expandedPositionId === p.id;

              return (
                <Box key={p.id}>
                  <ListItem
                    sx={{
                      bgcolor: isExpanded ? "action.selected" : "transparent",
                      borderBottom: "1px solid #eee",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      py: 2,
                    }}
                    secondaryAction={!isMobile ? PositionActions(p) : null}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <ListItemText
                        primary={`${p.name} (Máx: ${p.maxSelections} | Peso: ${p.pointWeight}) Ordem: ${p.displayOrder}`}
                        primaryTypographyProps={{ fontWeight: "bold" }}
                        sx={{
                          flexGrow: 1,
                          pr: isMobile ? 0 : 2,
                          mr: isMobile ? 1 : 0,
                        }}
                      />
                      {isMobile && PositionActions(p)}
                    </Box>

                    {/* Chips (Tags) para os Jogadores */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        width: "100%",
                        mt: isMobile ? 1 : 0,
                      }}
                    >
                      {playersInPosition.length > 0 ? (
                        playersInPosition.map((player) => (
                          <Chip
                            key={player.id}
                            label={player.name}
                            size="small"
                            color="default"
                            variant="outlined"
                            onDelete={() => setDeletingPlayer(player)}
                            deleteIcon={<CloseIcon />}
                            onClick={() => handleOpenEditPlayer(player)}
                            icon={<AccountCircleIcon />}
                            sx={{
                              fontSize: "0.7rem",
                              height: "24px",
                              "& .MuiChip-deleteIcon": { fontSize: "1rem" },
                              cursor: "pointer",
                            }}
                          />
                        ))
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          Nenhum item associado.
                        </Typography>
                      )}
                      {/* Botão de expansão (mantido no final) */}
                      {playersInPosition.length > 0 && (
                        <IconButton
                          onClick={() => handleTogglePlayerList(p.id)}
                          size="small"
                          sx={{ p: 0, ml: 0.5, alignSelf: "flex-end" }}
                        >
                          {isExpanded ? (
                            <ExpandMoreIcon fontSize="small" />
                          ) : (
                            <ChevronRightIcon fontSize="small" />
                          )}
                        </IconButton>
                      )}
                    </Box>
                  </ListItem>

                  {/* Lista de Jogadores Aninhada (inalterada) */}
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <List
                      component="div"
                      disablePadding
                      sx={{ bgcolor: "action.hover" }}
                    >
                      {playersInPosition.map((player) => (
                        <ListItem
                          key={player.id}
                          sx={{ pl: 4, py: 0.5 }}
                          secondaryAction={
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEditPlayer(player)}
                            >
                              <EditIcon
                                sx={{
                                  fontSize: 16,
                                  color: "#008870",
                                  "&:hover": { color: "#005848ff" },
                                }}
                              />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={player.name}
                            secondary={player.description || "Sem descrição"}
                            secondaryTypographyProps={{ variant: "caption" }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              );
            })}
          </List>
        )}
      </Box>
      {/* Modal de Edição (PUT) da Posição (inalterado) */}
      <Dialog
        open={!!editingPosition}
        onClose={() => setEditingPosition(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Editar Posição: {editingPosition?.name}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Novo Nome da Posição"
            fullWidth
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            margin="normal"
          />
          <TextField
            label="Novo Peso"
            fullWidth
            type="number"
            inputProps={{ min: 0.1, step: 0.1 }}
            value={editPointWeight}
            onChange={(e) => setEditPointWeight(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditingPosition(null)}
            sx={{
              color: "#008870",
              borderColor: "#008870",
              "&:hover": {
                color: "#005848ff",
                borderColor: "#005848ff",
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEditPosition}
            variant="contained"
            color="primary"
            sx={{
              backgroundColor: "#008870",
              "&:hover": {
                backgroundColor: "#005848ff",
              },
            }}
          >
            Salvar Alterações
          </Button>
        </DialogActions>
      </Dialog>
      {/* Modal de Edição de Jogador (PUT) (inalterado) */}
      <Dialog
        open={!!editingPlayer}
        onClose={() => setEditingPlayer(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Editar Item: {editingPlayer?.name}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Nome do Item"
            fullWidth
            value={editPlayerName}
            onChange={(e) => setEditPlayerName(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            label="Descrição do Item (Opcional)"
            fullWidth
            multiline
            rows={2}
            value={editPlayerDescription}
            onChange={(e) => setEditPlayerDescription(e.target.value)}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditingPlayer(null)}
            sx={{
              color: "#008870",
              borderColor: "#008870",
              "&:hover": {
                color: "#005848ff",
                borderColor: "#005848ff",
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleEditPlayer}
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            sx={{
              backgroundColor: "#008870",
              "&:hover": {
                backgroundColor: "#005848ff",
              },
            }}
          >
            Salvar Item
          </Button>
        </DialogActions>
      </Dialog>
      {/* ⭐️ Modal de Adição de Jogador (POST) - Substituindo Grid */}
      <Dialog
        open={isPlayerModalOpen}
        onClose={handleClosePlayerModal}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={isPostingBatch}
      >
        <DialogTitle>
          Adicionar Novo Item à Competição "{competitionTitle}"
          <IconButton
            aria-label="close"
            onClick={handleClosePlayerModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
            disabled={isPostingBatch}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isPostingBatch ? (
            <Box sx={{ width: "100%", mt: 2 }}>
              <Typography variant="body1" gutterBottom>
                Enviando lote de {newPlayersBatch.length} itens...
              </Typography>
              <LinearProgress variant="determinate" value={postingProgress} />
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ mt: 1 }}
              >
                Progresso: {postingProgress}%
              </Typography>
              <Alert severity="info" sx={{ mt: 2 }}>
                Por favor, aguarde a conclusão do envio. Não feche esta janela.
              </Alert>
            </Box>
          ) : (
            // Form Fields - SUBSTITUINDO GRID
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2, // Simula spacing={2}
                "& > div": {
                  width: "100%", // Todos os campos são fullWidth
                },
              }}
            >
              <FormControl fullWidth margin="normal" required>
                <InputLabel id="position-select-label">Posição</InputLabel>
                <Select
                  labelId="position-select-label"
                  value={selectedPositionId}
                  label="Posição"
                  onChange={(e) =>
                    setSelectedPositionId(e.target.value as string)
                  }
                  disabled={positions.length === 0}
                >
                  {positions.length === 0 && (
                    <MenuItem disabled>Nenhuma posição cadastrada.</MenuItem>
                  )}
                  {positions.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name} (Máx. Seleções: {p.maxSelections})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Nome do Item"
                fullWidth
                value={newPlayerName}
                onChange={(e) => setNewPlayerName(e.target.value)}
                margin="normal"
                required
              />
              <TextField
                label="Descrição do Item (Opcional)"
                fullWidth
                multiline
                rows={2}
                value={newPlayerDescription}
                onChange={(e) => setNewPlayerDescription(e.target.value)}
                margin="normal"
              />
              <Box sx={{ width: "100%" }}>
                <Alert severity="info">
                  Itens prontos para envio:{" "}
                  <Chip
                    label={newPlayersBatch.length}
                    color="primary"
                    size="small"
                  />
                </Alert>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {isPostingBatch ? (
            <Button disabled>Aguarde o envio...</Button>
          ) : (
            <>
              <Button
                onClick={handleClosePlayerModal}
                sx={{
                  color: "#008870",
                  "&:hover": {
                    color: "#005848ff",
                  },
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAddPlayerToBatch}
                variant="outlined"
                color="success"
                startIcon={<AddIcon />}
                disabled={!newPlayerName || !selectedPositionId}
              >
                Adicionar Mais um Item
              </Button>
              <Button
                onClick={handlePostPlayersBatch}
                variant="contained"
                color="success"
                startIcon={<SaveIcon />}
                disabled={newPlayersBatch.length === 0}
              >
                Salvar Todos ({newPlayersBatch.length})
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      {/* Modal de Confirmação de Exclusão de Jogador (inalterado) */}
      <Dialog
        open={!!deletingPlayer}
        onClose={() => setDeletingPlayer(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirmar Exclusão de Item</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Tem certeza que deseja DELETAR o Item{" "}
            <Typography component="span" fontWeight="bold">
              {deletingPlayer?.name}
            </Typography>
            ? Esta ação é irreversível.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeletingPlayer(null)}
            sx={{
              color: "#008870",
              "&:hover": {
                color: "#005848ff",
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleDeletePlayer}
            variant="contained"
            color="error"
          >
            Deletar
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar de Feedback (inalterado) */}
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={handleCloseFeedback}
      >
        <Alert
          onClose={handleCloseFeedback}
          severity={feedback.severity}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompetitionPositionManager;
